import { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import { stateOption } from "@/lib/constants";

export const LivingDetails = () => {
	const { formValues, setFormValue, setFormError } = useFormDataStore();
	const { enabledFields, setFormErrors, setValidateFormDetails } = useFormStateStore();

	const { livingStatus, state } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		livingStatus: false,
		state: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// State Change Handlers
	const handleStateChange = useCallback(
		(value: string) => {
			setFormValue("state", value);
			setErrors((prev) => ({ ...prev, state: !value }));
		},
		[setFormValue, setFormError, setErrors],
	);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			livingStatus: enabledFields.livingStatus?.length == 0 ? false : !livingStatus,
			state: !state,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [livingStatus, state]);

	return (
		<div className="jF-space-y-4">
			{enabledFields.livingStatus?.length != 0 && (
				<RadioGroupField
					id="livingStatus"
					label="What is your current living status?"
					value={livingStatus || ""}
					onChange={(value) => {
						setFormValue("livingStatus", value);
						setErrors((prev) => ({ ...prev, livingStatus: !value }));
					}}
					required
					error={errors.livingStatus ? "This field is required" : ""}
					gridCols={2}
					className="jF-mt-4 jF-justify-center jF-w-full"
					options={enabledFields.livingStatus
						.filter((s) => s.value)
						.map((s) => ({
							id: s.value,
							value: s.value,
							label: s.option,
						}))}
				/>
			)}

			{stateOption.length > 1 && (
				<RadioGroupField
					id="state"
					label="Please choose a state"
					value={state || ""}
					onChange={(value) => handleStateChange(value)}
					required
					error={errors.state ? "This field is required" : ""}
					gridCols={4}
					showIcons={false}
					className="jF-mt-4 jF-justify-center jF-w-full"
					options={stateOption
						.filter((s) => s.value)
						.map((s) => ({
							id: s.value,
							value: s.value,
							label: s.option,
						}))}
				/>
			)}
		</div>
	);
};
